import { useMutation } from '@tanstack/react-query'

import { useFindProfile } from '@hooks/profile'
import { api } from '@services/apiClient'
import { queryClient } from '@services/queryClient'

import { useFindBarriersQueries } from './queries'

interface IDeleteBarrierParams {
  taskId: number
  barrierId: number
}

export function useDeleteBarrier() {
  const { data: user } = useFindProfile()
  const { getBarriersQuery } = useFindBarriersQueries()
  const siteId = user && user.selectedSite.id

  async function deleteBarrier({ barrierId, taskId }: IDeleteBarrierParams) {
    const url = `sites/${siteId}/tasks/${taskId}/barriers/${barrierId}`
    const { data } = await api.delete(url)
    return data
  }

  return useMutation({
    mutationFn: deleteBarrier,
    onMutate: handleOptimisticDeleteBarrier,
    onError: (_err, _barrier, context) => {
      if (!context) return

      const { previousBarrier, barrierKey } = context
      queryClient.setQueryData(barrierKey, previousBarrier)
    },
    onSettled: (_barriers, _err, _variables, context) =>
      queryClient.invalidateQueries({
        queryKey: context?.barrierKey,
      }),
  })

  async function handleOptimisticDeleteBarrier({
    taskId,
    barrierId,
  }: IDeleteBarrierParams) {
    const barrierQuery = await getBarriersQuery(taskId)

    const [barrierKey, barrierResult] = barrierQuery
    const previousBarrier = barrierResult

    const updatedBarrierList = barrierResult?.filter(
      (barrier) => barrier.id !== barrierId,
    )

    queryClient.setQueryData(barrierKey, () => updatedBarrierList)

    return { barrierKey, updatedBarrierList, previousBarrier }
  }
}
